/**
 * @file pages/well/PhotoAuditPage.js
 * @description Well Butler — "Photo audit" (plan 06 §C.2 #4).
 *
 * Snap the wellhead (or pick a photo) → POST /api/v1/well/photo-audit → a list of
 * identified components, plus hazard flags on the severity scale, with confidence
 * and the verify line. The backend NEVER errors on a bad image — a blurry/blind
 * frame comes back as a low-confidence result we surface honestly.
 *
 * The server reads the photo with real AI when its AI key is set, and with an
 * honest empty stand-in when it is not. The answer says which (modelMeta.stub),
 * and the screen only shows the "not wired yet" note when the stand-in answered.
 * Photos are sent inline as data-URLs; real capture should upload via
 * core.storage and send storageKeys (the pipeline already accepts both).
 */
import React, { useState, useRef } from 'react';
import { Button, Card, Badge } from '../../design-system';
import { wellApi } from '../../lib/wellApi';
import { pushHistory } from '../../lib/wellHistory';
import {
  WellPage, ResultHeader, Disclaimer, ErrorNote,
} from './wellShared';

/*****************************************************************
 *  SAFETY-FLAG WORDS — MATCH THE SERVER'S 1-TO-4 SCALE           *
 *  The server ranks every flag 1 (note) to 4 (danger) — see      *
 *  server photo/core/severityScale.js. Each rank gets one plain  *
 *  word here and one color; the word always shows with the color *
 *  (color is never the only signal).                             *
 *****************************************************************/

// WHY (2026-10-07, James): defect #171 — the screen had a 5-step list topped by
// the joke "Call an ambulance", while the server only ever sends ranks 1-4. So a
// real DANGER (water near electric) showed as "Serious", and rank 5 never came.
// Plain words, matched to the real ranks, no jokes.
const SEVERITY_COLOR = {
  1: 'gray',     // INFO
  2: 'yellow',   // ADVISORY
  3: 'orange',   // WARNING
  4: 'red',      // DANGER
};
const SEVERITY_WORD = {
  1: 'Note',
  2: 'Worth a look',
  3: 'Problem — fix it',
  4: 'Danger — make it safe first',
};
const HIGHEST_SEVERITY_RANK = 4;   // anything ranked higher is shown as Danger

/* ---- severityRankOf(hazard) ------------------------------------
   The flag's rank, kept inside 1..4 so it always has a word.      */
function severityRankOf(hazard) {
  const rawRank = Number(hazard.severityRank) || 1;
  return Math.min(Math.max(rawRank, 1), HIGHEST_SEVERITY_RANK);
}

/* ---- cameFromStandIn(result) -----------------------------------
   True when the server says no real AI read the photo (no key).   */
function cameFromStandIn(result) {
  const modelInfo = result && result.modelMeta;
  return !!(modelInfo && modelInfo.stub);
}

export default function PhotoAuditPage() {
  const [photo, setPhoto] = useState(null); // { dataUrl }
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  /**
   * Downscale + JPEG-compress on the phone BEFORE upload. A raw phone photo is
   * 5–10 MB; the upload pipe (nginx 1 MB default + server JSON limit) rejects
   * that, and the AI vision tier downscales to ~1.1 MP anyway — so we cap the
   * longest edge at 1280 px / quality 0.78, which lands ~300–600 KB with zero
   * loss of audit usefulness.
   */
  function compressImage(file, maxEdge = 1280, quality = 0.78) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image — try a different photo.')); };
      img.src = url;
    });
  }

  function onPick(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setResult(null); setError(null);
    compressImage(file)
      .then((dataUrl) => setPhoto({ dataUrl }))
      .catch((err) => setError(err.message || 'Could not read that image.'));
  }

  async function run() {
    if (!photo) return;
    setLoading(true); setError(null);
    try {
      // v1 stub accepts images:[{url}] — send the data-URL inline. Real capture
      // should upload to core.storage and pass photoStorageKeys instead.
      const env = await wellApi.photoAudit({ images: [{ url: photo.dataUrl }] });
      if (env.success) {
        setResult(env.data);
        const n = (env.data.components || []).length;
        const h = (env.data.hazards || []).length;
        pushHistory({
          kind: 'audit',
          title: 'Photo audit',
          summary: `${n} part${n === 1 ? '' : 's'} seen · ${h} flag${h === 1 ? '' : 's'}`,
          confidence: env.data.confidence,
          persistedId: env.meta && env.meta.persistedId,
        });
      } else {
        setError(env.error || { message: 'Could not read that photo.' });
      }
    } catch (e) {
      setError({ message: e.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <WellPage
      title="Photo audit"
      intro="Take a clear photo of the wellhead and the gear around it. I'll list the parts I can see and flag anything that looks unsafe."
    >
      <Card variant="elevated" padding="md">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPick}
          style={{ display: 'none' }}
        />

        {photo ? (
          <img
            src={photo.dataUrl}
            alt="Wellhead to audit"
            style={{ width: '100%', borderRadius: 'var(--radius-lg)', display: 'block', marginBottom: 'var(--space-3)' }}
          />
        ) : (
          <div
            style={{
              height: 180, borderRadius: 'var(--radius-lg)', border: '2px dashed var(--border-default)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-tertiary)', marginBottom: 'var(--space-3)', textAlign: 'center', padding: 'var(--space-3)',
            }}
          >
            No photo yet — tap below to take or choose one.
          </div>
        )}

        <Button fullWidth variant="secondary" size="lg" onClick={() => fileRef.current && fileRef.current.click()}>
          {photo ? 'Retake / choose another' : 'Take or choose a photo'}
        </Button>

        {photo && (
          <Button fullWidth size="lg" loading={loading} onClick={run} style={{ marginTop: 'var(--space-3)' }}>
            Audit this photo
          </Button>
        )}
      </Card>

      {/* WHY (2026-10-07, James): defect #171 — this note used to show ALWAYS,
          even when the server reads photos with real AI. Now it shows only when
          the answer says the stand-in (no AI key) produced it. Honest UI. */}
      {result && cameFromStandIn(result) && (
        <Card variant="flat" padding="sm" style={{ marginTop: 'var(--space-3)' }}>
          <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            ⚠ Not read by AI: photo reading is not switched on for this server, so this result is an
            empty stand-in with low confidence. Check the wellhead with your own eyes.
          </p>
        </Card>
      )}

      {error && <div style={{ marginTop: 'var(--space-4)' }}><ErrorNote error={error} /></div>}

      {result && <AuditResult data={result} />}
    </WellPage>
  );
}

function AuditResult({ data }) {
  const components = Array.isArray(data.components) ? data.components : [];
  const hazards = Array.isArray(data.hazards) ? data.hazards : [];
  const sorted = hazards.slice().sort((a, b) => (b.severityRank || 0) - (a.severityRank || 0));

  return (
    <div style={{ marginTop: 'var(--space-5)' }}>
      <ResultHeader
        answer={`${components.length} part${components.length === 1 ? '' : 's'} seen, ${hazards.length} flag${hazards.length === 1 ? '' : 's'}`}
        sub={data.needsHumanReview ? 'Low certainty — a human should eyeball this one.' : undefined}
        confidence={data.confidence}
      />

      {sorted.length > 0 && (
        <Card variant="elevated" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
          <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-3)' }}>Safety flags</div>
          {sorted.map((h, i) => {
            const rank = severityRankOf(h);
            return (
              <div key={i} style={{ padding: 'var(--space-2) 0', borderTop: i ? '1px solid var(--border-default)' : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)' }}>
                  <span style={{ fontWeight: 'var(--weight-semibold)', fontSize: 'var(--text-body-sm)' }}>
                    {SEVERITY_WORD[rank] || h.severity || 'Flag'}
                  </span>
                  <Badge color={SEVERITY_COLOR[rank] || 'gray'} variant="subtle" size="sm">
                    {Math.round((h.confidence || 0) * 100)}% sure
                  </Badge>
                </div>
                <p style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                  {h.message || h.ruleId}
                </p>
              </div>
            );
          })}
        </Card>
      )}

      <Card variant="outline" padding="md" style={{ marginBottom: 'var(--space-3)' }}>
        <div style={{ fontWeight: 'var(--weight-bold)', marginBottom: 'var(--space-2)' }}>Parts I could see</div>
        {components.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: 'var(--text-body-sm)' }}>
            Nothing recognized in this frame. Try a clearer, closer shot in good light.
          </p>
        ) : (
          components.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-2)', padding: 'var(--space-1) 0' }}>
              <span style={{ fontSize: 'var(--text-body-sm)', color: 'var(--text-primary)' }}>
                {c.label}{c.category && c.category !== 'UNKNOWN' ? <em style={{ color: 'var(--text-tertiary)', fontStyle: 'normal' }}> · {c.category}</em> : null}
              </span>
              <Badge color="gray" variant="subtle" size="sm">{Math.round((c.confidence || 0) * 100)}%</Badge>
            </div>
          ))
        )}
      </Card>

      <Disclaimer text={data.disclaimer} />
    </div>
  );
}
